import React from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { ApolloProvider, useQuery, gql } from '@apollo/client';
import { IntlProvider } from 'react-intl';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import CfaResults from '../components/results/CfaResults';
import * as cfaActionCreators from '../../../../actions/cfaActionCreators';
import { updateCompany } from '../../../../actions/companyActionCreators';
import { CircularProgress } from '@material-ui/core';
import configureStore from '../../../../store/findBobPublicStore';
import client from '../../../../lib/apolloClient';
import translations from '../../../../lib/i18n/translations.json';

const GetCashFlow = gql`
	query cashFlow($friendlyName: String) {
	cashFlows{
		id
		name
		numberOfBuyers
		cashFlowLength
		sellerAverageCashFlow
		buyer1AverageCashFlow
		buyer2AverageCashFlow
		createdAt
		friendlyName
	}
	company {
		id
		cash_flow_analysis_word
		cash_flow_analysis_word_fr
		cashflow_payout_word
		show_branch_expenses
		features
	}
		getCurrentUser {
			id
			motivation
		}
    cashFlow(friendlyName: $friendlyName) {
			id
			name
			friendlyName
			createdAt
			cashFlowLength
			cashFlowProduction
			cashFlowProductionShift  # Add This to edit Production Shifts In Year cant be greater than length
			sellerT12Revenue
			sellerFiveYearGrowthRate
			numberOfBuyers
			buyer1T12Revenue
			buyer1FiveYearGrowthRate
			buyer1PercentagePurchasing
			buyer2T12Revenue
			buyer2FiveYearGrowthRate
			buyer2PercentagePurchasing
			buyerHasLumpSumPayment
			buyerLumpSumAmount
			buyerLumpSumYear
			buyersFinancingAmount
			buyerFinancingYears
			buyerHasFixedPayments
			buyerFixedPaymentYears
			buyerFixedPaymentAmount
			buyerFixedPaymentStartYear
			buyerHasContinuingPayments
			buyerContinuingPaymentYears
			buyerContinuingPaymentStartYear
			buyerContinuingPaymentAmount
			buyerContinuingPayments
			sellerAverageCashFlow
			buyer1AverageCashFlow
			buyer2AverageCashFlow
			sellerManualTaxRate
			sellerTaxRate
			buyer1ManualTaxRate
			buyer1TaxRate
			buyer2ManualTaxRate
			buyer2TaxRate
			cashFlowFinancingInterestRate
			buyer1BreakEven
			buyer2BreakEven
			hasCheckSwap
			checkSwapYear
			checkSwapAnnualBonus
			checkSwapLoanEndYear
			checkSwapLoanRepaymentAmount
			buyer1AnnualExpenses
			buyer1ExpenseGrowth
			buyer2AnnualExpenses
			buyer2ExpenseGrowth
			purchasePrice
			lifetimeCapitalGainsException
			cashflowSellerLifetimeCapitalGainsTax
			consultingTotalCompensation
			consultingLength
			sellerNoteAmount
			sellerNoteTerm
			sellerNoteInterestRate
			sellerNotePaymentFrequency
			sellerNoteDefaultAmmortization
			bdRetentionRate
			illustration {
				sellerCashFlow {
					revenueGrowth
					personalProduction
					acquiredProduction
					cashFlowProductionShift
					payoutRate
					payout
					lumpSumPaymentReceived
					fixedPaymentReceived
					contingentPaymentReceived
					bookSalePaymentReceived
					pretaxNetIncome
					taxes
					totalTaxes
					downPayment
					afterTaxNetIncome
					freeCashFlow
					buyerHasFixedPayments
					taxRate
					transitionAssistanceLoanRepayment
					checkSwapPayment
					consultingAgreement
					consultingAgreementTaxes
					sellerNotePayment
					sellerCarriedNoteInterest
					sellerCarriedNotePrincipal
					earnOutTaxes
					downpaymentTaxes
					branchRetentionTaxes
					sellerNoteInterestTaxes
					sellerNotePrincipalTaxes
					transitionAssistanceLoanBonus
					cumulativeProceeds
				}
				buyer1CashFlow {
					revenueGrowth
					personalProduction
					aquiredProduction
					totalProduction
					payoutRate
					grossRevenue
					payout
					bookBuyPaymentTotal
					fixedPayment
					contingentPayment
					pretaxNetIncome
					taxes
					afterTaxNetIncome
					lumpSumPaymentOutOfPocket
					successionLoanRepayment
					debtServiceCoverageRatio
					freeCashFlow
					taxRate
					transitionAssistanceBonus
					branchExpenses
					consultingAgreement
					sellerNoteInterestPayment
					sellerNotePayment
					sellerNoteAmortization
					netIncome
					downpaymentLoanInterestTax
					downpaymentAmortizationTax
					taxableIncome
					downPaymentNote
					attritionRate
				}
				buyer2CashFlow {
					revenueGrowth
					grossRevenue
					personalProduction
					aquiredProduction
					totalProduction
					payoutRate
					payout
					bookBuyPaymentTotal
					fixedPayment
					contingentPayment
					pretaxNetIncome
					taxes
					afterTaxNetIncome
					lumpSumPaymentOutOfPocket
					successionLoanRepayment
					debtServiceCoverageRatio
					freeCashFlow
					taxRate
					transitionAssistanceBonus
					branchExpenses
					consultingAgreement
					sellerNoteInterestPayment
					sellerNotePayment
					sellerNoteAmortization
					netIncome
					downpaymentLoanInterestTax
					downpaymentAmortizationTax
					taxableIncome
					downPaymentNote
					attritionRate
				}
			}
		}
	}
`;

const CfaEditContainer = props => {
	const { params } = props.match || {};
	const { id } = params || {};
	const cashFlowId = props.cashFlowId || id;
	let query = props.query;
	
	if(isEmpty(query)){
		query = {  ...query, updating: true, };
	}
	if(cashFlowId) {
		if(query.id != cashFlowId || isEmpty(query)){
			query = {  ...query, updating: true, };
		}
		query = {  ...query, friendlyName: cashFlowId, };
	}

	const { loading, data } = useQuery(GetCashFlow, {
		variables: query,
		onCompleted: (data) => {
			props.updateCompany({ ...data.company, features: data?.company?.features });
			if(!query.updating){
				return;
			}
			props.updateCfaResults(data);
			let { illustration, ...q } = get(data, 'cashFlow', {})

			props.updateCfaQuery({...q, motivation: get(data, 'getCurrentUser.motivation', 'succession'), updating: false});
		}
	});

	if (loading) {
		return <CircularProgress />;
	}

	return (
		<ConnectedCfaResults company={data?.company} />
	);
};

// Connect CfaResults to Redux
const ConnectedCfaResults = connect((state, props) => ({
  result: {...get(state, 'cfa.result', {})},
  query: {...get(state, 'cfa.query', {})},
  company: props.company,
}))(CfaResults);

const mapStateToProps = (state, props) => {
  return {
    result: {...get(state, 'cfa.result', {})},
		query: {...get(state, 'cfa.query', {})},
  };
};

const ConnectedCfaEditContainer = connect(mapStateToProps, { ...cfaActionCreators, updateCompany})(
  CfaEditContainer
);

// Wrapper that provides all necessary providers
export default (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  const store = configureStore(props);
  const locale = window.locale;
  
  return (
    <Provider store={store}>
      <IntlProvider locale={locale} messages={translations[locale] || translations.en}>
        <ApolloProvider client={client}>
          <ConnectedCfaEditContainer {...props} />
        </ApolloProvider>
      </IntlProvider>
    </Provider>
  );
}; 